import { useState, useEffect, useRef } from 'react'
import { lightTheme } from '@/customTheme'
import { useTranslation, Trans } from 'react-i18next'
import ModalHeader from '@/components/ModalHeader'
import { useStyletron } from 'styletron-react'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { SignInManager } from '../../SignInManager'
import { UserInfo } from '@/common/interfaces'

interface SignInWithEmailVerifyCodeProps {
  email: string
  onBack: () => void
  onSuccess: (user: UserInfo) => void
}

function SignInWithEmailVerifyCode({ email, onBack, onSuccess }: SignInWithEmailVerifyCodeProps) {
  const { t } = useTranslation()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [timeLeft, setTimeLeft] = useState(60)
  const [css] = useStyletron()
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft])

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index) => {
    if(index !== 0) return
    const paste = e.clipboardData.getData('text')
    if (paste.length === 6 && /^\d{6}$/.test(paste)) {
      e.preventDefault()
      const newCode = paste.split('')
      setCode(newCode)
      inputRefs.current[5]?.focus()
      if (error) {
        setError('')
      }
    }
  }

  const handleCodeChange = (value: string, index: number) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1) // Take only the last digit

    setCode(newCode)
    // Clear error when user starts typing
    if (error) {
      setError('')
    }

    // Auto-focus to next input
    if (newCode[index] && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      const newCode = [...code]
      if (!newCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      } else {
        newCode[index] = ''
        setCode(newCode)
      }
    }
  }

  const handleVerifyCode = async () => {
    setIsSubmitting(true)
    setError('')
    try {
      const result = await SignInManager.getInstance().sendOTPEmail(code.join(''), email)
      onSuccess(result)
    } catch (error: any) {
      console.error('Error verifying code:', error)
      setError('The code you entered is incorrect.')
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    if (timeLeft > 0 || isSubmitting) return
    
    setIsSubmitting(true)
    setError('')
    try {
      await SignInManager.getInstance().loginWithEmail(email)
      // Only reset on success:
      setTimeLeft(60)
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (error: any) {
      console.error('Error resending code:', error)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isContinueDisabled = code.join('').length !== 6 || isSubmitting
  const isResendDisabled = timeLeft > 0 || isSubmitting

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <ModalHeader
        hasNavigation
        handleNavigation={onBack}
        disableNavigation={isSubmitting}
        title={t('Check your inbox')}
        style={{ padding: 0 }}
        titleStyle={{ textTransform: 'none', marginLeft: '-20px' }}
      />

      <h3
        style={{
          marginBottom: 0,
          marginTop: '8px',
          ...lightTheme.typography.Small14regular,
          fontWeight: 400,
          color: lightTheme.colors.blackGray,
          textAlign: 'center',
        }}
      >
        <Trans
          i18nKey="We sent a 6-digit code to <bold>{{email}}</bold>. Enter it here to continue."
          values={{ email }}
          components={{ bold: <span style={{ fontWeight: 600 }} /> }}
        />
      </h3>

      {/* OTP Input Fields */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginTop: '32px',
          marginBottom: error ? '8px' : '24px',
          justifyContent: 'center',
        }}
      >
        {code.map((digit, index) => (
          <input
            key={index}
            ref={el => (inputRefs.current[index] = el)}
            type="text"
            value={digit}
            onChange={e => handleCodeChange(e.target.value, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            maxLength={1}
            disabled={isSubmitting}
            className={css({
              width: '48px',
              height: '48px',
              padding: '12px',
              border: `1px solid ${
                error ? lightTheme.colors.primary : lightTheme.colors.grayScale100}`,
              borderRadius: '8px',
              textAlign: 'center',
              ...lightTheme.typography.Small14regular,
              fontWeight: 400,
              color: isSubmitting ? lightTheme.colors.text.text_200_disable : lightTheme.colors.blackGray,
              background: lightTheme.colors.white,
              transition: 'all 0.2s ease',
              '::placeholder': {
                color: lightTheme.colors.grayScale400,
              },
              ':hover': {
                borderColor: lightTheme.colors.grayScale300,
              },
              ':focus': {
                borderColor: lightTheme.colors.blackGray,
              },
              cursor: isSubmitting ? 'not-allowed' : 'text',
              pointerEvents: isSubmitting ? 'none' : 'auto',
            })}
          />
        ))}
      </div>

      {/* Error Message */}
      {error && (
        <p
          style={{
            marginBottom: '12px',
            marginTop: 0,
            ...lightTheme.typography.Small11medium,
            color: lightTheme.colors.negative,
            fontWeight: 400,
            textAlign: 'center',
          }}
        >
          {t(error)}
        </p>
      )}

      {/* Continue Button */}
      <ButtonCustom
        kind="primary"
        type={SizeButton.LARGE}
        onClick={handleVerifyCode}
        disabled={isContinueDisabled}
        isLoading={isSubmitting}
        style={isSubmitting ? { background: `${lightTheme.colors.primary}!important` } : {}}
      >
        {isSubmitting ? '' : t('Continue')}
      </ButtonCustom>

      {/* Resend Section */}
      <div
        style={{
          marginTop: '24px',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            marginTop: 0,
            ...lightTheme.typography.Small12regular,
            lineHeight: '22px',
            color: lightTheme.colors.grayScale400,
            fontWeight: 400,
          }}
        >
          {t("Didn't get it?")}
        </p>
        <p
          style={{
            marginBottom: 0,
            marginTop: 0,
            ...lightTheme.typography.Small12regular,
            lineHeight: '22px',
            color: lightTheme.colors.grayScale400,
            fontWeight: 400,
          }}
        >
          {t('Check your spam or')}{' '}
          <span
            onClick={handleResendCode}
            style={{
              color: isSubmitting ? lightTheme.colors.grayScale400 : lightTheme.colors.blackGray,
              cursor: isResendDisabled ? 'not-allowed' : 'pointer',
              textDecoration: isResendDisabled ? 'none' : 'underline',
            }}
          >
            {isResendDisabled
              ? t('resend in {{seconds}} seconds', { seconds: timeLeft })
              : t('resend code')}
          </span>
        </p>
      </div>
    </div>
  )
}

export default SignInWithEmailVerifyCode
